import { Warning, Gavel, ShieldWarning, Calculator } from "@phosphor-icons/react";

const eur = (v) => (v == null ? "—" : "€ " + Number(v).toLocaleString("it-IT", { maximumFractionDigits: 0 }));
const IMPACT_CLS = {
    CRITICAL: "bg-red-500/[0.15] text-red-300 border-red-500/40",
    HIGH: "bg-orange-500/[0.15] text-orange-300 border-orange-500/40",
    MEDIUM: "bg-yellow-500/[0.12] text-yellow-300 border-yellow-500/40",
    LOW: "bg-white/[0.05] text-slate-300 border-white/10",
};

function ExecCell({ label, value, accent, big }) {
    return (
        <div className="px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{label}</div>
            <div className={`font-mono-tight font-bold ${big ? "text-2xl" : "text-[15px]"}`} style={{ color: accent || "var(--acc-default)" }}>{value}</div>
        </div>
    );
}

export default function EngineV4Panel({ engine }) {
    if (!engine?.executive) return null;
    const ex = engine.executive;
    const npatColor = ex.net_profit_after_tax > 0 ? "var(--acc-green)" : "var(--acc-red)";
    const sens = engine.sensitivity;
    return (
        <div className="space-y-6 mb-6" data-testid="engine-v4">
            {/* ===== EXECUTIVE INVESTMENT DECISION ===== */}
            <div className="rounded-2xl border border-white/[0.12] bg-[#0B111F] overflow-hidden fade-up" data-testid="engine-executive-box">
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 border-b border-white/[0.08] bg-white/[0.02]">
                    <div className="flex items-center gap-2">
                        <Calculator size={17} weight="fill" className="text-[#22D3EE]" />
                        <span className="text-[12px] font-bold uppercase tracking-[0.14em] text-white">Investment Decision · Engine V4</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/[0.12] text-emerald-300">Calcolo deterministico</span>
                    </div>
                    {ex.fiscal_review_required && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border border-amber-500/40 bg-amber-500/[0.12] text-amber-300" data-testid="engine-tax-review-badge">
                            Fiscal profile: da confermare
                        </span>
                    )}
                </div>
                {ex.do_not_bid && (
                    <div className="flex items-center gap-3 px-5 py-3.5 bg-red-500/[0.12] border-b border-red-500/40" data-testid="engine-do-not-bid">
                        <Gavel size={20} weight="fill" className="text-red-400 shrink-0" />
                        <div className="text-sm font-bold text-red-300">
                            DO NOT BID — Il prezzo massimo economico ({eur(ex.max_economic_bid)}) è inferiore all&apos;offerta minima ({eur(ex.minimum_auction_bid)}) rispetto ai tuoi target (profitto ≥ {eur(ex.targets?.min_profit)}, ROI ≥ {ex.targets?.min_roi_pct}%).
                        </div>
                    </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y divide-white/[0.05]">
                    <ExecCell label="Utile netto post-tasse" value={eur(ex.net_profit_after_tax)} accent={npatColor} big />
                    <ExecCell label="ROI post-tasse" value={`${ex.roi_after_tax_pct}%`} accent={npatColor} big />
                    <ExecCell label="Max Economic Bid" value={eur(ex.max_economic_bid)} accent="var(--acc-violet)" big />
                    <ExecCell label="ROI annualizzato" value={`${ex.annualized_roi_pct}%`} big />
                    <ExecCell label="Offerta minima asta" value={eur(ex.minimum_auction_bid)} />
                    <ExecCell label="Rivendita realistica" value={eur(ex.realistic_resale_value)} />
                    <ExecCell label="Capitale totale investito" value={eur(ex.total_investment)} />
                    <ExecCell label="Holding stimato" value={`${ex.expected_holding_months} mesi`} />
                    <ExecCell label="Profitto ante imposte" value={eur(ex.net_profit_before_tax)} />
                    <ExecCell label="Fiscalità stimata" value={"− " + eur(ex.estimated_tax).replace("€ ", "€ ")} accent="var(--acc-amber)" />
                    <ExecCell label="Break-even rivendita" value={eur(ex.breakeven_sale_price)} />
                    <ExecCell label="Break-even acquisto" value={eur(ex.breakeven_purchase_price)} />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* ===== BID SCENARIOS ===== */}
                <div className="rounded-2xl border border-white/[0.1] bg-[#0B111F] p-5" data-testid="engine-bid-scenarios">
                    <div className="overline mb-3">Scenari di aggiudicazione (rivendita realistica)</div>
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-wider text-slate-400">
                            <tr><th className="text-left pb-2">Aggiudicazione</th><th className="text-right pb-2">Prezzo</th><th className="text-right pb-2">Utile netto</th><th className="text-right pb-2">ROI</th></tr>
                        </thead>
                        <tbody className="font-mono-tight">
                            {engine.bid_scenarios?.map((b) => (
                                <tr key={b.label} className="border-t border-white/[0.05]">
                                    <td className="py-2 text-slate-200 font-sans font-semibold">{b.label}</td>
                                    <td className="py-2 text-right text-white">{eur(b.bid)}</td>
                                    <td className={`py-2 text-right font-bold ${b.npat > 0 ? "text-emerald-400" : "text-red-400"}`}>{eur(b.npat)}</td>
                                    <td className="py-2 text-right text-slate-200">{b.roi_pct}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* ===== SENSITIVITY MATRIX ===== */}
                <div className="rounded-2xl border border-white/[0.1] bg-[#0B111F] p-5 overflow-x-auto" data-testid="engine-sensitivity">
                    <div className="overline mb-3">Sensitivity — Utile netto post-tasse</div>
                    <table className="w-full text-[11px] font-mono-tight min-w-[420px]">
                        <thead>
                            <tr>
                                <th className="text-left pb-1.5 text-slate-400 font-sans text-[10px] uppercase">Rivendita ↓ / Acquisto →</th>
                                {sens?.bid_axis?.map((b) => <th key={b} className="text-right pb-1.5 text-slate-300">{Math.round(b / 1000)}k</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {sens?.sale_axis?.map((s, i) => (
                                <tr key={s} className="border-t border-white/[0.04]">
                                    <td className="py-1.5 text-slate-300">{Math.round(s / 1000)}k</td>
                                    {sens.matrix?.[i]?.map((v, j) => (
                                        <td key={j} className={`py-1.5 text-right font-bold ${v > 0 ? "text-emerald-400" : "text-red-400"}`}>
                                            {Math.round(v / 1000)}k
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ===== FASE 3: RENOVATION / HOLDING / CONFIDENCE ===== */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {engine.renovation && (
                    <div className="rounded-2xl border border-white/[0.1] bg-[#0B111F] p-5" data-testid="engine-renovation">
                        <div className="overline mb-1">Renovation budget · {engine.renovation.level}</div>
                        <div className="font-mono-tight font-bold text-xl text-white mb-2">
                            {eur(engine.renovation.total)}
                            {engine.renovation.eur_sqm && <span className="text-xs text-slate-400 font-normal"> · ~€{Math.round(engine.renovation.eur_sqm)}/mq</span>}
                        </div>
                        {engine.renovation.breakdown?.length > 0 ? (
                            <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
                                {engine.renovation.breakdown.map((b) => (
                                    <div key={b.label} className="flex justify-between text-[12px]">
                                        <span className="text-slate-300">{b.label}</span>
                                        <span className="font-mono-tight text-slate-100">{eur(b.value)}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-[12px] text-slate-400">{engine.renovation.label || "Nessun intervento previsto"}</p>
                        )}
                        <p className="text-[10px] text-slate-500 mt-3">{engine.renovation.basis}</p>
                    </div>
                )}
                {engine.holding?.monthly_items?.length > 0 && (
                    <div className="rounded-2xl border border-white/[0.1] bg-[#0B111F] p-5" data-testid="engine-holding">
                        <div className="overline mb-1">Costi di detenzione</div>
                        <div className="font-mono-tight font-bold text-xl text-white mb-2">
                            {eur(engine.holding.monthly_total)}<span className="text-xs text-slate-400 font-normal">/mese × {ex.expected_holding_months} mesi</span>
                        </div>
                        <div className="space-y-1">
                            {engine.holding.monthly_items.map((i) => (
                                <div key={i.label} className="flex justify-between text-[12px]">
                                    <span className="text-slate-300">{i.label} <span className="text-slate-500">({i.source_type === "CALCULATED" ? "calc." : "stima"})</span></span>
                                    <span className="font-mono-tight text-slate-100">{eur(i.value)}</span>
                                </div>
                            ))}
                        </div>
                        <p className="text-[10px] text-slate-500 mt-3">{engine.holding.basis}</p>
                    </div>
                )}
                {engine.confidence?.drivers && (
                    <div className="rounded-2xl border border-white/[0.1] bg-[#0B111F] p-5" data-testid="engine-confidence">
                        <div className="overline mb-1">Confidence (calcolata sui dati)</div>
                        <div className="font-mono-tight font-bold text-xl mb-2" style={{ color: engine.confidence.level === "HIGH" ? "var(--acc-green)" : engine.confidence.level === "MEDIUM" ? "var(--acc-yellow)" : "var(--acc-red)" }}>
                            {engine.confidence.score}/100 · {engine.confidence.level}
                        </div>
                        <div className="space-y-1.5">
                            {engine.confidence.drivers.map((d) => (
                                <div key={d.label} className="flex items-center justify-between text-[12px]">
                                    <span className={d.ok ? "text-slate-300" : "text-slate-500"}>{d.label}</span>
                                    <span className={`font-bold text-[10px] uppercase ${d.ok ? "text-emerald-400" : "text-red-400"}`}>{d.ok ? "OK" : "manca"}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* ===== ASSUMPTION REGISTER ===== */}
            <div className="rounded-2xl border border-white/[0.1] bg-[#0B111F] p-5" data-testid="engine-assumptions">
                <div className="flex items-center gap-2 mb-3">
                    <ShieldWarning size={16} weight="fill" className="text-amber-400" />
                    <span className="overline">Assunzioni da verificare prima dell&apos;offerta</span>
                </div>
                <div className="space-y-2">
                    {engine.assumptions?.map((a, i) => (
                        <div key={i} className="flex items-start gap-3 text-[13px] text-slate-200">
                            <span className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${IMPACT_CLS[a.impact] || IMPACT_CLS.LOW}`}>{a.impact}</span>
                            {a.text}
                        </div>
                    ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-4 flex items-start gap-1.5">
                    <Warning size={12} className="shrink-0 mt-0.5" />
                    Strumento di decision support: i numeri contrassegnati come stime/assunzioni richiedono verifica professionale (commercialista, geometra, legale) prima di partecipare all&apos;asta.
                </p>
            </div>
        </div>
    );
}
